/**
 * Plugin contract. Each plugin is a self-contained module that can:
 *   - enrich a skill (add fields)
 *   - contribute filter facets
 *   - render an extra section in the detail panel
 *
 * Adding a new dimension = creating a file in lib/plugins/built-in/ and
 * calling registerPlugin() in lib/plugins/index.ts.
 */

import type { ReactNode } from "react";
import type { Skill } from "../types";
export interface FacetFilterDef {
  id: string;
  label: string;
  match: (skill: Skill) => boolean;
}

export interface SkillPlugin {
  id: string;
  name: string;
  description: string;
  /** Pure — do not mutate the supplied Skill; return a new Skill with derived fields. */
  enrich?(skill: Skill): Skill & Record<string, unknown>;
  /** Add key/value facets for filtering. */
  facets?(skill: Skill): Record<string, string | number | boolean>;
  /** Optional filter toggles shown under // Plugin facets in FilterRail. */
  facetFilters?: FacetFilterDef[];
  /** Optional detail-panel section. Receives the current skill. */
  DetailSection?(props: { skill: Skill }): ReactNode;
}

const registry = new Map<string, SkillPlugin>();

export function registerPlugin(plugin: SkillPlugin): void {
  registry.set(plugin.id, plugin);
}

export function getPlugin(id: string): SkillPlugin | undefined {
  return registry.get(id);
}

export function listPlugins(): SkillPlugin[] {
  return Array.from(registry.values());
}

export function applyEnrichments(skill: Skill): Skill {
  let s = skill;
  for (const p of registry.values()) {
    if (p.enrich) s = p.enrich(s);
  }
  return s;
}

export function collectFacets(skill: Skill): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const p of registry.values()) {
    if (p.facets) Object.assign(out, p.facets(skill));
  }
  return out;
}
